import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { ArrowBack, VisibilityOutlined } from "@mui/icons-material";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import { CircularProgress, Tooltip } from "@mui/material";

import SearchBar from "../../../components/common/SearchBar/SearchBar";
import FilterBar from "../../../components/common/FilterBar/FilterBar";
import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import DDateInput from "../../../components/Dashboard/DashboardInputs/DDateInput";
import DropdownBtnOutlined from "../../../components/Dashboard/DropdownButtons/DropdownBtnOutlined/DropdownBtnOutlined";
import PaginantionComponent from "../../../components/common/Pagination/PaginationComponent";
import { API_ENDPOINT } from "../../../services/api/resources";
import classes from "./SalesInvoiceTab.module.css";
import Dbutton from "../../../components/Dashboard/DashboardButtons/Dbutton";

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return [value];
  return [];
};

const parseMaybeJson = (value) => {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const formatAmount = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const numeric = Number(
    String(value)
      .replace(/[, ]/g, "")
      .replace(/[^\d.-]/g, ""),
  );
  if (!Number.isFinite(numeric)) return String(value);
  return numeric.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const valueOrDash = (value) =>
  value === null || value === undefined || String(value).trim() === ""
    ? "-"
    : value;

const getInvoiceTotalCount = (payload, invoices = []) =>
  payload?.total_count ??
  payload?.count ??
  payload?.total ??
  payload?.data?.total_count ??
  payload?.data?.count ??
  payload?.data?.total ??
  invoices.length;

const normalizeInvoiceItems = (invoice) => {
  const fromDirect = toArray(
    parseMaybeJson(
      invoice?.items ||
        invoice?.item_details ||
        invoice?.line_items ||
        invoice?.invoice_items ||
        invoice?.zoho_data_items,
    ),
  );

  return fromDirect.map((item, index) => ({
    line_no: item?.line_no ?? index + 1,
    item_name: item?.item_name || item?.name || item?.description || "-",
    item_description: item?.item_description || item?.description || "-",
    quantity: item?.quantity ?? item?.qty ?? "-",
    rate: item?.rate ?? "-",
    amount: item?.amount ?? item?.total ?? "-",
  }));
};

const normalizeInvoicesResponse = (payload) => {
  const results =
    payload?.response ||
    payload?.results ||
    payload?.data?.response ||
    payload?.response?.results ||
    payload?.data?.results ||
    payload?.invoices ||
    payload?.response?.invoices ||
    payload?.data?.invoices ||
    payload;

  const rows = Array.isArray(results) ? results : [];
  return rows.map((invoice, index) => {
    const normalizedInvoice =
      invoice?.zoho_datas ||
      invoice?.zoho_data ||
      invoice?.data ||
      invoice ||
      {};
    const itemsSource =
      invoice?.zoho_data_items ||
      normalizedInvoice?.zoho_data_items ||
      normalizedInvoice?.items ||
      normalizedInvoice?.line_items ||
      normalizedInvoice?.invoice_items;
    const items = normalizeInvoiceItems({
      ...normalizedInvoice,
      items: itemsSource,
    });
    const firstItemName =
      normalizedInvoice?.item_name ||
      normalizedInvoice?.item_details?.[0]?.item_name ||
      normalizedInvoice?.line_items?.[0]?.item_name ||
      normalizedInvoice?.invoice_items?.[0]?.item_name ||
      items?.[0]?.item_name ||
      "-";

    return {
      ...invoice,
      ...normalizedInvoice,
      _rowKey:
        normalizedInvoice?.id ||
        normalizedInvoice?.invoice_no ||
        normalizedInvoice?.invoice_number ||
        normalizedInvoice?.po_number ||
        index,
      invoice_no:
        normalizedInvoice?.invoice_no ||
        normalizedInvoice?.invoice_number ||
        normalizedInvoice?.bill_number ||
        "-",
      invoice_date:
        normalizedInvoice?.invoice_date || normalizedInvoice?.bill_date || "-",
      due_date: normalizedInvoice?.due_date || "-",
      po_number:
        normalizedInvoice?.po_number ||
        normalizedInvoice?.pi_po_number ||
        normalizedInvoice?.purchase_order_no ||
        "-",
      gstin:
        normalizedInvoice?.gstin ||
        normalizedInvoice?.gst ||
        normalizedInvoice?.tax_identification_number ||
        "-",
      created_at:
        normalizedInvoice?.created_at || normalizedInvoice?.created_on || "-",
      item_name: firstItemName,
      total_amount:
        normalizedInvoice?.total_amount ??
        normalizedInvoice?.grand_total ??
        normalizedInvoice?.amount ??
        "-",
      balance_due: normalizedInvoice?.balance_due ?? "-",
      subtotal: normalizedInvoice?.subtotal ?? "-",
      payment_terms: normalizedInvoice?.payment_terms ?? "-",
      place_of_supply: normalizedInvoice?.place_of_supply ?? "-",
      customer_name: normalizedInvoice?.customer_name ?? "-",
      customer_address: normalizedInvoice?.customer_address ?? "-",
      company_name: normalizedInvoice?.company_name ?? "-",
      company_address: normalizedInvoice?.company_address ?? "-",
      bank_name: normalizedInvoice?.bank_name ?? "-",
      account_number: normalizedInvoice?.account_number ?? "-",
      ifsc_code: normalizedInvoice?.ifsc_code ?? "-",
      terms_and_conditions: normalizedInvoice?.terms_and_conditions ?? "-",
      items,
    };
  });
};

const SalesInvoiceTab = () => {
  const loginData = useSelector((state) => state.loginData.value);
  const authToken = loginData?.auth_token;
  const buyerToken =
    loginData?.buyer_token ||
    loginData?.user?.buyer_token ||
    loginData?.user?.token ||
    loginData?.organization_token ||
    loginData?.user_token;

  const [allInvoices, setAllInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedInvoiceLoading, setSelectedInvoiceLoading] = useState(false);
  const [selectedInvoiceError, setSelectedInvoiceError] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [startDateSearchQuery, setStartDateSearchQuery] = useState({
    isValid: true,
    value: "",
  });
  const [endDateSearchQuery, setEndDateSearchQuery] = useState({
    isValid: true,
    value: "",
  });
  const [invoiceFilter, setInvoiceFilter] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [detailView, setDetailView] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const fetchInvoices = async () => {
      if (!buyerToken) {
        setAllInvoices([]);
        setFilteredInvoices([]);
        setTotalItems(0);
        return;
      }

      setLoading(true);
      setErrorMessage("");
      try {
        const response = await axios.post(
          API_ENDPOINT.get_zoho_datas,
          { buyer_token: buyerToken, page: pageNumber, page_size: pageSize },
          authToken
            ? { headers: { Authorization: `Bearer ${authToken}` } }
            : undefined,
        );
        const invoices = normalizeInvoicesResponse(response?.data);
        setAllInvoices(invoices);
        setFilteredInvoices(invoices);
        setTotalItems(getInvoiceTotalCount(response?.data, invoices));
      } catch (error) {
        setAllInvoices([]);
        setFilteredInvoices([]);
        setTotalItems(0);
        setErrorMessage(
          error?.response?.data?.response ||
            error?.response?.data?.message ||
            error?.message ||
            "Unable to load sales invoices",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, [authToken, buyerToken, pageNumber, pageSize]);

  useEffect(() => {
    const query = String(searchQuery || "")
      .trim()
      .toLowerCase();
    const startDateValue = startDateSearchQuery?.value
      ? new Date(startDateSearchQuery.value)
      : null;
    const endDateValue = endDateSearchQuery?.value
      ? new Date(endDateSearchQuery.value)
      : null;
    const hasStartDate =
      startDateValue && !Number.isNaN(startDateValue.getTime());
    const hasEndDate = endDateValue && !Number.isNaN(endDateValue.getTime());

    const rows = allInvoices.filter((invoice) => {
      const matchesQuery =
        !query ||
        [
          invoice?.invoice_no,
          invoice?.po_number,
          invoice?.gstin,
          invoice?.item_name,
          invoice?.company_name,
          invoice?.customer_name,
          invoice?.total_amount,
        ]
          .filter(Boolean)
          .some((field) => String(field).toLowerCase().includes(query));

      const createdAt = invoice?.created_at
        ? new Date(invoice.created_at)
        : null;
      const hasValidCreatedAt = createdAt && !Number.isNaN(createdAt.getTime());
      const matchesStartDate =
        !hasStartDate || !hasValidCreatedAt || createdAt >= startDateValue;
      const matchesEndDate =
        !hasEndDate || !hasValidCreatedAt || createdAt <= endDateValue;

      return matchesQuery && matchesStartDate && matchesEndDate;
    });

    setFilteredInvoices(rows);
  }, [
    allInvoices,
    searchQuery,
    startDateSearchQuery,
    endDateSearchQuery,
    invoiceFilter,
  ]);

  const hasActiveClientFilters =
    Boolean(String(searchQuery || "").trim()) ||
    Boolean(startDateSearchQuery?.value) ||
    Boolean(endDateSearchQuery?.value) ||
    Boolean(String(invoiceFilter || "").trim());
  const totalItemsCount = hasActiveClientFilters
    ? filteredInvoices.length
    : totalItems;
  const totalPages =
    totalItemsCount > 0 ? Math.ceil(totalItemsCount / pageSize) : 0;
  const currentPage =
    totalPages > 0 ? Math.min(Number(pageNumber) || 1, totalPages) : 0;
  const currentRows = filteredInvoices;

  const currentItemPreview = (invoice) => {
    const items = Array.isArray(invoice?.items) ? invoice.items : [];
    if (items.length === 0) return valueOrDash(invoice?.item_name);
    
    const firstItem = items[0] || {};
    const name = valueOrDash(firstItem?.item_name);
    const desc = valueOrDash(firstItem?.item_description);
    
    let first = name;
    if (name === "-" && desc !== "-") {
      first = desc;
    } else if (name !== "-" && desc !== "-" && name !== desc) {
      first = `${name} - ${desc}`;
    }
    
    return items.length > 1 ? `${first} +${items.length - 1} more` : first;
  };

  const handleViewInvoice = async (invoice) => {
    setSelectedInvoiceError("");
    setDetailView(true);
    setSelectedInvoice({
      ...invoice,
      items: normalizeInvoiceItems(invoice),
    });
    setSelectedInvoiceLoading(false);
  };

  const handleBackToList = () => {
    setDetailView(false);
    setSelectedInvoice(null);
    setSelectedInvoiceError("");
  };

  const renderSummaryRow = (label, value) => (
    <div className={classes.summaryCard}>
      <div className={classes.summaryLabel}>{label}</div>
      <div className={classes.summaryValue}>{valueOrDash(value)}</div>
    </div>
  );

  return (
    <div className={classes.page}>
      <div className={classes.shell}>
        {!detailView ? (
          <>
            <div className={classes.pageHeader}>
              <HeaderTitle
                customclass={classes.dashHead}
                titleText="Sales Invoice"
              />
            </div>

            <div className={classes.search_add_wrapper}>
              <div
                className={`${classes.flex} ${classes.flex_align_end} ${classes.search_add_wrapper_content_left}`}
              >
                <SearchBar
                  value={searchQuery}
                  onChange={(query) => {
                    setSearchQuery(query);
                    setPageNumber(1);
                  }}
                  placeholder="Search invoice, PO, GST or item"
                />
                {/* <div className={classes.dateFiltersGroup}>
                  <DDateInput
                    customClass={classes.dateFilters}
                    type="date"
                    label="Start Date"
                    disableFuture={true}
                    onChange={(value) => {
                      setPageNumber(1);
                      setStartDateSearchQuery({
                        isValid: value ? Boolean(value) : true,
                        value,
                      });
                    }}
                    value={startDateSearchQuery}
                    error={!startDateSearchQuery?.isValid}
                    errorMsg="From date is required"
                  />
                  <DDateInput
                    customClass={classes.dateFilters}
                    type="date"
                    label="End Date"
                    disableFuture={true}
                    fromDate={startDateSearchQuery.value}
                    onChange={(value) => {
                      setPageNumber(1);
                      setEndDateSearchQuery({
                        isValid: value ? Boolean(value) : true,
                        value,
                      });
                    }}
                    value={endDateSearchQuery}
                    error={!endDateSearchQuery?.isValid}
                    errorMsg="To date is required"
                  />
                </div> */}
              </div>

              {/* <div className={classes.rhsButtons}>
                <FilterBar
                  filterValue={invoiceFilter}
                  onFilterChange={(value) => {
                    setPageNumber(1);
                    setInvoiceFilter(value);
                  }}
                  options={[{ value: "", label: "All" }]}
                />
                <DropdownBtnOutlined
                  label="DOWNLOAD AS"
                  btnHeight="4rem"
                  btnWidth="18rem"
                  loadingState={loading}
                  disabled={currentRows.length === 0}
                  onClick={() => {}}
                  options={["CSV", "PDF"]}
                />
              </div> */}
            </div>

            <div className={classes.listCard}>
              {loading ? (
                <div className={classes.listHeader}>
                  <div className={classes.sectionHeading} />
                  <div className={classes.actionGroup}>
                    <CircularProgress size={22} />
                  </div>
                </div>
              ) : null}

              <div className={classes.tableWrap}>
                <table className={classes.table}>
                  <thead>
                    <tr>
                      <th className={`${classes.th} ${classes.stickyLeftHead}`}>
                        Supplier Invoice ID
                      </th>
                      <th className={classes.th}>PO No</th>
                      <th className={classes.th}>GSTIN</th>
                      <th className={classes.th}>Created Date</th>
                      <th className={classes.th}>Item / Description</th>
                      <th className={classes.th}>Total Amount</th>
                      <th className={`${classes.th} ${classes.stickyRightHead}`}>
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {!loading && currentRows.length === 0 ? (
                      <tr>
                        <td className={classes.td} colSpan={7}>
                          <div className={classes.emptyState}>
                            {errorMessage || "No sales invoice records found"}
                          </div>
                        </td>
                      </tr>
                    ) : null}

                    {currentRows.map((invoice) => (
                      <tr key={invoice._rowKey}>
                        <td className={`${classes.td} ${classes.stickyLeftCell}`}>
                          {invoice.invoice_no}
                        </td>
                        <td className={classes.td}>{invoice.po_number}</td>
                        <td className={classes.td}>{invoice.gstin}</td>
                        <td className={classes.td}>
                          {formatDateTime(invoice.created_at)}
                        </td>
                        <td className={`${classes.td} ${classes.truncate}`}>
                          {currentItemPreview(invoice)}
                        </td>
                        <td className={`${classes.td} ${classes.amount}`}>
                          {new Intl.NumberFormat("en-IN", {
                            style: "currency",
                            currency: "INR",
                          }).format(invoice.total_amount)}
                        </td>
                        <td className={`${classes.td} ${classes.stickyRightCell}`}>
                          <Tooltip
                            title="View invoice details"
                            arrow
                            slotProps={{
                              tooltip: {
                                sx: {
                                  fontSize: "1.2rem",
                                },
                              },
                            }}
                          >
                            <button
                              type="button"
                              className={classes.actionBtn}
                              onClick={() => handleViewInvoice(invoice)}
                            >
                              <VisibilityOutlined
                                sx={{ fontSize: "2rem", color: "#2563eb" }}
                              />
                            </button>
                          </Tooltip>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className={classes.paginationBar}>
                <PaginantionComponent
                  pageNumber={currentPage}
                  setPageNumber={(value) => {
                    if (value === "") return;
                    const parsed = parseInt(value, 10);
                    if (!Number.isNaN(parsed)) {
                      setPageNumber(parsed);
                    }
                  }}
                  currentPage={currentPage}
                  itemsPerPage={totalItemsCount}
                  totalItems={totalPages}
                  rowsPerPage={pageSize}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  onRowsPerPageChange={(event) => {
                    const newRows = parseInt(event.target.value, 10);
                    setPageSize(Number.isNaN(newRows) ? pageSize : newRows);
                    setPageNumber(1);
                  }}
                  previouPageClick={() =>
                    setPageNumber((prev) => Math.max(1, Number(prev) - 1))
                  }
                  nextPageClick={() =>
                    setPageNumber((prev) =>
                      Math.min(
                        totalPages || 1,
                        Number(prev) + 1,
                      ),
                    )
                  }
                  disablePrevious={currentPage <= 1}
                  disableNext={totalPages === 0 || currentPage >= totalPages}
                />
              </div>
            </div>
          </>
        ) : (
          <div className={classes.detailCard}>
            <div className={classes.detailHeader}>
              <div className={classes.sectionHeading}>
                <HeaderTitle
                  customclass={classes.sectionTitle}
                  titleText={`Invoice ${selectedInvoice?.invoice_no || ""}`}
                />
              </div>
              <div className={classes.actionGroup}>
                {selectedInvoiceLoading ? <CircularProgress size={22} /> : null}
                <Dbutton
                  label={"BACK"}
                  btnHeight="4rem"
                  btnWidth={"12rem"}
                  buyerBtn={true}
                  onClick={handleBackToList}
                />
              </div>
            </div>

            <div className={classes.detailBody}>
              {selectedInvoiceError ? (
                <div className={classes.emptyState}>{selectedInvoiceError}</div>
              ) : null}

              <div className={classes.summaryGrid}>
                {renderSummaryRow("Supplier Invoice ID", selectedInvoice?.invoice_no)}
                {renderSummaryRow("PO No", selectedInvoice?.po_number)}
                {renderSummaryRow(
                  "Total Amount",
                  formatAmount(selectedInvoice?.total_amount),
                )}
                {renderSummaryRow(
                  "Balance Due",
                  formatAmount(selectedInvoice?.balance_due),
                )}
              </div>

              <div className={classes.sectionGrid}>
                <div className={classes.infoCard}>
                  <div className={classes.infoCardHeader}>Invoice Details</div>
                  <div className={classes.infoList}>
                    {[
                      [
                        "Invoice Date",
                        formatDate(selectedInvoice?.invoice_date),
                      ],
                      ["Due Date", formatDate(selectedInvoice?.due_date)],
                      ["Payment Terms", selectedInvoice?.payment_terms],
                      ["Place of Supply", selectedInvoice?.place_of_supply],
                      [
                        "Created Date",
                        formatDateTime(selectedInvoice?.created_at),
                      ],
                      ["Total In Words", selectedInvoice?.total_in_words],
                    ].map(([key, value]) => (
                      <div className={classes.infoRow} key={key}>
                        <div className={classes.infoKey}>{key}</div>
                        <div className={classes.infoValue}>
                          {valueOrDash(value)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className={classes.infoCard}>
                  <div className={classes.infoCardHeader}>
                    Customer and Company
                  </div>
                  <div className={classes.infoList}>
                    {[
                      ["Customer Name", selectedInvoice?.customer_name],
                      ["Customer Address", selectedInvoice?.customer_address],
                      ["Company Name", selectedInvoice?.company_name],
                      ["Company Address", selectedInvoice?.company_address],
                      ["GSTIN", selectedInvoice?.gstin],
                    ].map(([key, value]) => (
                      <div className={classes.infoRow} key={key}>
                        <div className={classes.infoKey}>{key}</div>
                        <div className={classes.infoValue}>
                          {valueOrDash(value)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className={classes.sectionGrid}>
                <div className={classes.infoCard}>
                  <div className={classes.infoCardHeader}>Bank Details</div>
                  <div className={classes.infoList}>
                    {[
                      ["Bank Name", selectedInvoice?.bank_name],
                      ["Account Number", selectedInvoice?.account_number],
                      ["IFSC Code", selectedInvoice?.ifsc_code],
                    ].map(([key, value]) => (
                      <div className={classes.infoRow} key={key}>
                        <div className={classes.infoKey}>{key}</div>
                        <div className={classes.infoValue}>
                          {valueOrDash(value)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className={classes.infoCard}>
                  <div className={classes.infoCardHeader}>Amounts</div>
                  <div className={classes.infoList}>
                    {[
                      ["Subtotal", formatAmount(selectedInvoice?.subtotal)],
                      [
                        "Total Amount",
                        formatAmount(selectedInvoice?.total_amount),
                      ],
                      [
                        "Balance Due",
                        formatAmount(selectedInvoice?.balance_due),
                      ],
                    ].map(([key, value]) => (
                      <div className={classes.infoRow} key={key}>
                        <div className={classes.infoKey}>{key}</div>
                        <div className={classes.infoValue}>
                          {valueOrDash(value)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className={classes.infoCard}>
                <div className={classes.infoCardHeader}>Line Item Details</div>
                <div style={{ overflowX: "auto" }}>
                  <table className={classes.lineItemsTable}>
                    <thead>
                      <tr>
                        <th>Line No</th>
                        <th>Item / Description</th>
                        <th>Quantity</th>
                        <th>Rate</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(selectedInvoice?.items) &&
                      selectedInvoice.items.length > 0 ? (
                        selectedInvoice.items.map((item, index) => {
                          const name = valueOrDash(item?.item_name);
                          const desc = valueOrDash(item?.item_description);
                          
                          let displayText = name;
                          if (name === "-" && desc !== "-") {
                            displayText = desc;
                          } else if (name !== "-" && desc !== "-" && name !== desc) {
                            displayText = `${name} - ${desc}`;
                          }

                          return (
                          <tr key={`${item?.line_no || index}-${index}`}>
                            <td>{item?.line_no ?? index + 1}</td>
                            <td>{displayText}</td>
                            <td>{valueOrDash(item?.quantity)}</td>
                            <td>{formatAmount(item?.rate)}</td>
                            <td>{formatAmount(item?.amount)}</td>
                          </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={5}>
                            <div className={classes.emptyState}>
                              No line item details available
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className={classes.infoCard} style={{ marginTop: "1.4rem" }}>
                <div className={classes.infoCardHeader}>
                  Terms and Conditions
                </div>
                <div className={classes.infoList}>
                  <div className={classes.infoValue}>
                    {valueOrDash(selectedInvoice?.terms_and_conditions)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesInvoiceTab;
